import { WorkspaceFile } from '@/models/file';
import { LibraryService } from '@/services/library.service';
import { useQuery } from '@tanstack/react-query';
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import * as docx from 'docx-preview';
type CurrentDocType = WorkspaceFile | undefined;
const useCurrentDoc = (
  orgID: string,
  files: WorkspaceFile[],
  setFiles: Dispatch<SetStateAction<WorkspaceFile[]>>,
  isSearchingForFile = false
) => {
  const { id: slug } = useParams<{ id: string }>();
  const [currentDoc, setCurrentDoc] = useState<CurrentDocType>(undefined);
  const docContentRef = useRef<HTMLDivElement>(null);
  const [showContentsPreview, setShowContentsPreview] = useState(false);

  const shouldRenderDocContent = useCallback(
    (file: WorkspaceFile | undefined) => {
      return ['doc', 'docx', 'csv'].includes(file?.type || '');
    },
    []
  );

  // Sync currentDoc with files list when URL slug or files change
  // Note: currentDoc is intentionally NOT in dependencies to avoid a feedback loop
  // where clearing currentDoc would trigger this effect to re-set it from stale files
  useEffect(() => {
    if (files.length === 0) {
      setCurrentDoc(undefined);
      return;
    }
    const matchedFile = slug
      ? files.find((file) => file.slug === slug)
      : files[0];
    setCurrentDoc(matchedFile);
  }, [files, slug]);

  useEffect(() => {
    setShowContentsPreview(shouldRenderDocContent(currentDoc));
  }, [currentDoc, shouldRenderDocContent]);

  const isLoading = useUpdateCurrentDoc(
    currentDoc,
    setCurrentDoc,
    orgID,
    files,
    setFiles,
    docContentRef,
    isSearchingForFile
  );

  return {
    docContentRef,
    currentDoc,
    setCurrentDoc,
    isLoading,
    showContentsPreview,
  };
};

const shouldRenderDocContent = (file: CurrentDocType) => {
  return ['doc', 'docx', 'csv'].includes(file?.type || '');
};

const renderDocContent = async (
  file: WorkspaceFile,
  docContentRef: RefObject<HTMLDivElement>
) => {
  if (!docContentRef.current) {
    console.log('docContentRef is not set');
    return;
  }

  docContentRef.current.innerHTML = '';
  try {
    const response = await fetch(file.url);
    if (!response.ok) {
      throw new Error('Failed to fetch document for preview');
    }

    if (['doc', 'docx'].includes(file.type)) {
      const blob = await response.blob();
      docx.renderAsync(blob, docContentRef.current!);
    } else {
      const text = await response.text();
      docContentRef.current!.innerHTML = `<pre>${text}</pre>`;
    }
  } catch (e) {
    console.error(e);
    docContentRef.current!.innerHTML =
      '<p style="color:red">Failed to preview document.</p>';
  }
};

const useUpdateCurrentDoc = (
  currentDoc: CurrentDocType,
  setCurrentDoc: Dispatch<SetStateAction<CurrentDocType>>,
  orgID: string,
  files: WorkspaceFile[],
  setFiles: Dispatch<SetStateAction<WorkspaceFile[]>>,
  docContentRef: RefObject<HTMLDivElement>,
  isSearchingForFile: boolean
) => {
  const { id: slug } = useParams<{ id: string }>();
  const { isLoading } = useQuery<WorkspaceFile | undefined>({
    queryKey: ['file', slug],
    queryFn: async () => {
      let file = files.find((f) => f.slug === slug);
      if (docContentRef.current) {
        docContentRef.current.innerHTML = '';
      }
      setCurrentDoc(file);

      if (!slug) return undefined;
      if (typeof file === 'undefined') {
        return undefined;
      }

      const expiresMatch = file.url.match(/Expires=(\d+)/);
      if (expiresMatch) {
        const expiresTimestamp = parseInt(expiresMatch[1], 10);
        if (expiresTimestamp < Date.now() / 1000) {
          file = await LibraryService.getFile(file.id, orgID);
          if (typeof file !== 'undefined') {
            setFiles((prevFiles) => {
              const existingIndex = prevFiles.findIndex(
                (f) => f.id === file!.id
              );
              if (existingIndex !== -1) {
                const updatedFiles = [...prevFiles];
                updatedFiles[existingIndex] = file!;
                return updatedFiles;
              } else {
                return [...prevFiles, file!];
              }
            });
          }
        }
      }

      if (shouldRenderDocContent(file) && typeof file !== 'undefined') {
        await renderDocContent(file, docContentRef);
      }

      setCurrentDoc(file);

      return file;
    },
    enabled: !!slug && !!currentDoc,
    refetchOnWindowFocus: false,
  });

  return isLoading || isSearchingForFile;
};

export default useCurrentDoc;
